import React, { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, TextField, FormControlLabel, Switch } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import api from '../../api';

const TeamLeaveTab = ({ empId, activeMenu }) => {
  const currentYear = new Date().getFullYear();
  
  // Dynamically calculate yesterday's date in local time
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const endYear = yesterday.getFullYear();
  const endMonth = String(yesterday.getMonth() + 1).padStart(2, '0');
  const endDay = String(yesterday.getDate()).padStart(2, '0');
  const yesterdayStr = `${endYear}-${endMonth}-${endDay}`;

  const [dateRange, setDateRange] = useState({ start: `${currentYear}-01-01`, end: yesterdayStr });
  const [useTree, setUseTree] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const isOverview = activeMenu === 'balance_overview';

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [activeMenu, useTree]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (isOverview) {
        const res = await api.get(`/leave/team-balances/${empId}?tree=${useTree}`);
        setData(res.data || []);
      } else {
        const res = await api.get(`/leave/team-requests/${empId}?tree=${useTree}`);
        setData(res.data || []);
      }
    } catch (err) { console.error("Error fetching team data", err); }
    setLoading(false);
  };

  const renderStackedCell = (primary, secondary) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{primary || '--'}</Typography>
      <Typography variant="caption" color="text.secondary">{secondary || '--'}</Typography>
    </Box>
  );

  const rows = useMemo(() => {
    const map = {};
    if (isOverview) {
      data.forEach(b => {
        if (!map[b.emp_id]) map[b.emp_id] = { id: b.emp_id, emp_id: b.emp_id, employee_name: b.name, department: b.dept, designation: b.desig, CL: 0, NH: 0, AL: 0, ML: 0, EL: 0, CO: 0 };
        if (map[b.emp_id][b.leave_type] !== undefined) map[b.emp_id][b.leave_type] = (b.total_days || 0) - (b.used_days || 0);
      });
    } else {
      data.forEach(req => {
        if (!map[req.emp_id]) map[req.emp_id] = { id: req.emp_id, emp_id: req.emp_id, employee_name: req.employee_name, department: req.department, designation: req.designation, CL: 0, NH: 0, 'HALF-DAY': 0, WO: 0, AL: 0, LOP: 0, DLOP: 0, ML: 0, EL: 0, CO: 0 };
        if (req.start_date >= dateRange.start && req.start_date <= dateRange.end) {
          if (map[req.emp_id][req.leave_type] !== undefined) map[req.emp_id][req.leave_type] += Number(req.days) || 0;
        }
      });
    }
    return Object.values(map);
  }, [data, isOverview, dateRange]);

  const coreCols = [
    { field: 'emp_details', headerName: 'Employee & ID', width: 200, disableColumnMenu: false, valueGetter: (p, r) => `${r.employee_name} ${r.emp_id}`, renderCell: (p) => renderStackedCell(p.row.employee_name, p.row.emp_id) },
    { field: 'dept_details', headerName: 'Dept & Designation', width: 220, disableColumnMenu: true, valueGetter: (p, r) => `${r.department} ${r.designation}`, renderCell: (p) => renderStackedCell(p.row.department, p.row.designation) }
  ];

  const types = isOverview ? ['CL', 'NH', 'AL', 'ML', 'EL', 'CO'] : ['CL', 'NH', 'HALF-DAY', 'WO', 'AL', 'LOP', 'DLOP', 'ML', 'EL', 'CO'];
  const dynamicCols = types.map((t, index) => ({
    field: t, headerName: t, width: 90, type: 'number',
    disableColumnMenu: index !== types.length - 1 // Menu only active on the very last column
  }));

  const columns = [...coreCols, ...dynamicCols];

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fbfbfb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
          {isOverview ? "Balance Overview" : "Team Summary"}
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {!isOverview && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={dateRange.start} onChange={e => setDateRange({...dateRange, start: e.target.value})} />
              <TextField size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={dateRange.end} onChange={e => setDateRange({...dateRange, end: e.target.value})} />
            </Box>
          )}
          <FormControlLabel control={<Switch checked={useTree} onChange={(e) => setUseTree(e.target.checked)} color="primary" />} label="Team Tree" />
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, p: 2, height: 600 }}>
        <DataGrid
          loading={loading}
          rows={rows}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc', fontWeight: 'bold' },
            '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type': {
              position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#fff', boxShadow: '2px 0 4px -2px rgba(0,0,0,0.2)'
            }
          }}
        />
      </Box>
    </Paper>
  );
};

export default TeamLeaveTab;